import { useState } from "react";
import { Tooltip } from "antd";

interface SceneDetailItem {
  name: string;
  extractType: string;
  gid: string | number;
  objectid: string | number;
  geom: string;
}

type TabKey = "communityProfile" | "buildingProfile" | "facilities";

interface InfoRowProps {
  icon: string;
  label: string;
  value: React.ReactNode;
  tooltip?: React.ReactNode;
}

function InfoRow({ icon, label, value, tooltip }: InfoRowProps) {
  const content = (
    <div className={`flex items-center gap-1.5 ${tooltip ? "cursor-help" : ""}`}>
      <i className={`${icon} text-slate-400 text-[11px]`} />
      <span className="text-[11px] text-slate-600">
        {label}：<span className="font-medium text-slate-700">{value}</span>
      </span>
    </div>
  );

  if (tooltip) {
    return <Tooltip title={tooltip}>{content}</Tooltip>;
  }
  return content;
}

export function CommunityPanel({ scene }: { scene: SceneDetailItem }) {
  const [mainTab, setMainTab] = useState<TabKey>("communityProfile");

  const tabs = [
    { key: "communityProfile" as TabKey, label: "小区画像", icon: "ri-home-4-line" },
    { key: "buildingProfile" as TabKey, label: "楼栋画像", icon: "ri-building-line" },
    { key: "facilities" as TabKey, label: "配套设施", icon: "ri-store-2-line" },
  ];

  return (
    <>
      {/* Main Tabs */}
      <div className="flex border-b border-slate-200">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setMainTab(tab.key)}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium transition-colors cursor-pointer ${
              mainTab === tab.key
                ? "text-emerald-600 border-b-2 border-emerald-500 bg-emerald-50/50"
                : "text-slate-500 hover:text-slate-700 hover:bg-slate-50"
            }`}
          >
            <i className={`${tab.icon} text-[13px]`} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="max-h-[380px] overflow-y-auto">
        {mainTab === "communityProfile" && (
          <div className="px-4 py-3 space-y-3">
            {/* 基本信息 */}
            <div>
              <div className="flex items-center gap-1.5 mb-2">
                <i className="ri-information-line text-emerald-400 text-[13px]" />
                <span className="text-xs font-semibold text-slate-600">基本信息</span>
              </div>
              <div className="bg-emerald-50/50 rounded-lg px-3 py-3 space-y-2">
                <InfoRow icon="ri-home-4-line" label="小区名称" value={scene.name} />
                <InfoRow icon="ri-building-line" label="小区类型" value="住宅小区" />
                <InfoRow icon="ri-shield-check-line" label="封闭类型" value="开放" />
                <InfoRow icon="ri-git-branch-line" label="是否多期" value="是" />
                <InfoRow icon="ri-community-line" label="楼栋数" value="24栋" />
                <InfoRow icon="ri-user-line" label="总户数" value="456户" />
                <InfoRow icon="ri-calendar-line" label="建筑年代" value="1988年" />
              </div>
            </div>

            {/* 物业信息 */}
            <div>
              <div className="flex items-center gap-1.5 mb-2">
                <i className="ri-building-2-line text-emerald-400 text-[13px]" />
                <span className="text-xs font-semibold text-slate-600">物业信息</span>
              </div>
              <div className="bg-emerald-50/50 rounded-lg px-3 py-3 space-y-2">
                <InfoRow icon="ri-government-line" label="开发商" value="南京城镇建设开发有限公司" />
                <InfoRow icon="ri-ruler-line" label="容积率" value="1.5" />
                <InfoRow icon="ri-leaf-line" label="绿化率" value="25%" />
                <InfoRow icon="ri-user-settings-line" label="物业信息" value="街道办事处代管物业" />
                <InfoRow icon="ri-money-cny-circle-line" label="物业费" value="0.5元/m²·月" />
              </div>
            </div>

            {/* 停车信息 */}
            <div>
              <div className="flex items-center gap-1.5 mb-2">
                <i className="ri-parking-line text-emerald-400 text-[13px]" />
                <span className="text-xs font-semibold text-slate-600">停车信息</span>
              </div>
              <div className="bg-emerald-50/50 rounded-lg px-3 py-3 space-y-2">
                <InfoRow icon="ri-parking-box-line" label="停车场" value="地面停车场" />
                <InfoRow icon="ri-numbers-line" label="停车位数量" value="200个" />
                <InfoRow icon="ri-list-check" label="停车位类型" value="/" />
              </div>
            </div>

            {/* 地下空间 */}
            <div className="bg-emerald-50/50 rounded-lg px-3 py-2.5">
              <InfoRow icon="ri-arrow-down-circle-line" label="地下空间" value="/" />
            </div>
          </div>
        )}

        {mainTab === "buildingProfile" && (
          <div className="px-4 py-3 space-y-2">
            <div className="flex items-center gap-1.5 mb-2">
              <i className="ri-building-line text-emerald-400 text-[13px]" />
              <span className="text-xs font-semibold text-slate-600">楼栋列表（24栋）</span>
            </div>
            <div className="grid grid-cols-4 gap-2">
              {Array.from({ length: 24 }, (_, i) => (
                <Tooltip
                  key={i}
                  color="#fff"
                  overlayInnerStyle={{
                    background: "#fff",
                    border: "1px solid #d1fae5",
                    borderRadius: 10,
                    boxShadow: "0 4px 20px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.04)",
                    padding: "12px 14px",
                  }}
                  title={
                    <div className="min-w-[148px]">
                      <div className="flex items-center gap-1.5 mb-2.5 pb-2 border-b border-emerald-100">
                        <i className="ri-building-2-line text-emerald-500 text-[13px]" />
                        <span className="text-[12px] font-semibold text-slate-700">{i + 1}号楼</span>
                      </div>
                      <div className="space-y-1.5">
                        <div className="flex items-center gap-1.5">
                          <i className="ri-stack-line text-emerald-300 text-[10px]" />
                          <span className="text-[11px] text-slate-500">单元数</span>
                          <span className="text-[11px] font-medium text-slate-700 ml-auto">2</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <i className="ri-building-4-line text-emerald-300 text-[10px]" />
                          <span className="text-[11px] text-slate-500">楼层数</span>
                          <span className="text-[11px] font-medium text-slate-700 ml-auto">7</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <i className="ri-user-line text-emerald-300 text-[10px]" />
                          <span className="text-[11px] text-slate-500">户数</span>
                          <span className="text-[11px] font-medium text-slate-700 ml-auto">28</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <i className="ri-file-text-line text-emerald-300 text-[10px]" />
                          <span className="text-[11px] text-slate-500">产权</span>
                          <span className="text-[11px] font-medium text-slate-700 ml-auto">70年</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <i className="ri-road-map-line text-emerald-300 text-[10px]" />
                          <span className="text-[11px] text-slate-500">道路名</span>
                          <span className="text-[11px] font-medium text-slate-700 ml-auto">四条巷</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <i className="ri-door-open-line text-emerald-300 text-[10px]" />
                          <span className="text-[11px] text-slate-500">门牌号</span>
                          <span className="text-[11px] font-medium text-slate-700 ml-auto">{i + 1}号楼</span>
                        </div>
                      </div>
                    </div>
                  }
                >
                  <div className="flex flex-col items-center justify-center gap-1 bg-emerald-50/60 border border-emerald-200 rounded-lg px-2 py-3 cursor-pointer hover:bg-emerald-100 transition-colors">
                    <i className="ri-building-2-line text-emerald-500 text-lg" />
                    <span className="text-[11px] font-medium text-slate-700">{i + 1}号楼</span>
                  </div>
                </Tooltip>
              ))}
            </div>
          </div>
        )}

        {mainTab === "facilities" && (
          <div className="px-4 py-3 space-y-3">
            {/* 交通出行 */}
            <div>
              <div className="flex items-center gap-1.5 mb-2">
                <i className="ri-bus-line text-emerald-400 text-[13px]" />
                <span className="text-xs font-semibold text-slate-600">交通出行</span>
              </div>
              <div className="bg-emerald-50/50 rounded-lg px-3 py-3 space-y-2">
                <div className="flex items-start gap-1.5">
                  <i className="ri-subway-line text-slate-400 text-[11px] mt-0.5" />
                  <span className="text-[11px] text-slate-600">地铁：<span className="font-medium text-slate-700">距离地铁2/3号线大行宫站约663米</span></span>
                </div>
                <div className="flex items-start gap-1.5">
                  <i className="ri-bus-2-line text-slate-400 text-[11px] mt-0.5" />
                  <span className="text-[11px] text-slate-600">公交：<span className="font-medium text-slate-700">附近有科巷、常府街等多个公交站，线路包括1路、30路、40路、44路、49路、60路、95路等</span></span>
                </div>
              </div>
            </div>

            {/* 教育资源 */}
            <div>
              <div className="flex items-center gap-1.5 mb-2">
                <i className="ri-school-line text-emerald-400 text-[13px]" />
                <span className="text-xs font-semibold text-slate-600">教育资源</span>
              </div>
              <div className="bg-emerald-50/50 rounded-lg px-3 py-3 space-y-2">
                <div className="flex items-center gap-1.5">
                  <i className="ri-user-heart-line text-slate-400 text-[11px]" />
                  <span className="text-[11px] text-slate-600">对口幼儿园：<span className="font-medium text-slate-700">南京市五老村幼儿园</span></span>
                </div>
                <div className="flex items-center gap-1.5">
                  <i className="ri-book-open-line text-slate-400 text-[11px]" />
                  <span className="text-[11px] text-slate-600">对口小学：<span className="font-medium text-slate-700">南京市五老村小学</span></span>
                </div>
                <div className="flex items-center gap-1.5">
                  <i className="ri-book-3-line text-slate-400 text-[11px]" />
                  <span className="text-[11px] text-slate-600">对口中学：<span className="font-medium text-slate-700">南京第六中学</span></span>
                </div>
              </div>
            </div>

            {/* 商业与生活配套 */}
            <div>
              <div className="flex items-center gap-1.5 mb-2">
                <i className="ri-shopping-bag-3-line text-emerald-400 text-[13px]" />
                <span className="text-xs font-semibold text-slate-600">商业与生活配套</span>
              </div>
              <div className="bg-emerald-50/50 rounded-lg px-3 py-3 space-y-2">
                <div className="flex items-start gap-1.5">
                  <i className="ri-store-2-line text-slate-400 text-[11px] mt-0.5" />
                  <span className="text-[11px] text-slate-600">商场/超市：<span className="font-medium text-slate-700">周边有苏果超市、沃尔玛超市等，以及大洋百货、金鹰国际购物中心等大型商场</span></span>
                </div>
              </div>
            </div>

            {/* 医疗资源 */}
            <div>
              <div className="flex items-center gap-1.5 mb-2">
                <i className="ri-hospital-line text-emerald-400 text-[13px]" />
                <span className="text-xs font-semibold text-slate-600">医疗资源</span>
              </div>
              <div className="bg-emerald-50/50 rounded-lg px-3 py-3 space-y-2">
                <div className="flex items-start gap-1.5">
                  <i className="ri-psychotherapy-line text-slate-400 text-[11px] mt-0.5" />
                  <span className="text-[11px] text-slate-600">三甲医院：<span className="font-medium text-slate-700">距离最近的综合性医院是东部战区总医院（秦淮医疗区），约537米</span></span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
